/**
 * Hook para gerenciamento de permissões de usuários
 * 
 * v2.0.0 - Refatorado para consumir PermissionsContext (Context Provider)
 * Antes: cada instância fazia seu próprio fetch RPC (N chamadas)
 * Agora: re-exporta do contexto centralizado (1 chamada)
 * 
 * Regras de acesso (mantidas no Provider):
 * - root_admin e tenant_admin: acesso total a todos os menus
 * - agent: acesso apenas ao chat
 * - tenant_supervisor: acesso baseado no perfil de permissões
 *   - Menus 'company' e 'settings' são SEMPRE bloqueados (hardcoded)
 *   - Se não tem perfil associado, bloqueia tudo
 */

// Re-exporta o hook do contexto centralizado
export { usePermissionsContext as usePermissions } from '@/contexts/PermissionsContext';

// Tipo de role do aplicativo
export type AppRole = 'root_admin' | 'tenant_admin' | 'tenant_supervisor' | 'agent' | 'user';

// Interface de permissão por menu
export interface MenuPermission {
  menu_key: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

// Interface do contexto de permissões
export interface PermissionsState {
  loading: boolean;
  userRole: AppRole | null;
  permissions: MenuPermission[];
  permissionProfileId: string | null;
}

// Menus disponíveis no sistema para controle de permissões
// Cada menu tem sua própria key para controle granular
export const AVAILABLE_MENUS = [
  // Menus do módulo Conversas
  { key: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
  { key: 'chat', label: 'Conversas', icon: 'MessagesSquare' },
  { key: 'queue', label: 'Fila de Atendimento', icon: 'ListTodo' },
  { key: 'distribution', label: 'Distribuição', icon: 'ActivitySquare' },             // v1.3.0 - Permissão separada
  { key: 'distribution_queues', label: 'Filas de Distribuição', icon: 'Layers' },     // v1.3.0 - Permissão separada
  { key: 'inboxes', label: 'Inbox', icon: 'MessageSquare' },
  // v1.0.0 — Permissão dedicada da tela de Templates (/tenant/templates).
  // Antes a rota era liberada para todos; o backfill deixa a chave habilitada
  // nos perfis existentes para evitar regressão.
  { key: 'templates', label: 'Templates', icon: 'LayoutTemplate' },
  { key: 'contacts', label: 'Contatos', icon: 'Contact' },
  // v1.0.0 - Permissão granular para gerenciamento de Tags de Contato
  // Controla criar/editar/excluir tags em /tenant/contacts (Liberado por padrão)
  { key: 'contact_tags', label: 'Tags de Contato', icon: 'Tags' },
  { key: 'campaigns', label: 'Campanhas', icon: 'Megaphone' },
  { key: 'kanban', label: 'Kanban', icon: 'Kanban' },
  { key: 'departments', label: 'Departamentos', icon: 'Network' },
  { key: 'routing', label: 'Açoes rápidas/Macros', icon: 'Route' },
  { key: 'flows', label: 'Fluxos', icon: 'GitBranch' },
  { key: 'agents', label: 'Agentes', icon: 'Users' },
  // Menus do módulo CRM/Negócios
  { key: 'crm_dashboard', label: 'Dashboard CRM', icon: 'LayoutDashboard' },
  { key: 'crm_deals', label: 'Negócios', icon: 'Briefcase' },
  { key: 'crm_customers', label: 'Clientes', icon: 'Users' },
  { key: 'crm_sellers', label: 'Vendedores', icon: 'UserCheck' },
  // v2.3.0 - Permissão granular para o menu Metas (CrmSidebar /tenant/crm/goals)
  { key: 'crm_goals', label: 'Metas CRM', icon: 'Target' },
  { key: 'crm_categories', label: 'Categorias', icon: 'FolderTree' },
  { key: 'crm_products', label: 'Produtos', icon: 'Package' },
  { key: 'crm_prices', label: 'Tabelas de Preço', icon: 'DollarSign' },
  { key: 'crm_workspaces', label: 'Workspaces', icon: 'Layers' },
  // Permissão granular para Playbooks de Atividades (Cadências de Vendas) — Fase 1
  // Controla acesso a /tenant/crm/playbooks (lista + editor)
  { key: 'crm_playbooks', label: 'Playbook de Vendas', icon: 'ListChecks' },
  // Permissão granular para o menu "Listagem Atividades" (ActivitiesSidebar /tenant/activities/list)
  { key: 'activities_list', label: 'Listagem Atividades', icon: 'ListChecks' },
  // Item 2.12 — Permissão granular para Cadência ativa em negócios
  // can_view: ver aba "Cadência" no DealModal
  // can_edit: pular passo (can_skip_cadence_step)
  // can_delete: cancelar cadência inteira
  { key: 'crm_cadence', label: 'Cadência de Negócios', icon: 'Zap' },
  // v2.1.0 - Permissões granulares para gerenciamento estrutural do Kanban CRM
  // Desacopladas de `crm_deals` (CRUD de negócios) — pipelines/fases são configuração
  { key: 'crm_pipelines', label: 'Pipelines CRM', icon: 'GitBranch' },
  { key: 'crm_phases', label: 'Fases do Kanban CRM', icon: 'Columns' },
  // v1.4.0 - Permissão granular para Templates de Contrato
  { key: 'crm_contract_templates', label: 'Template Contratos', icon: 'FileSignature' },
  // v1.5.0 - Permissão para Contratos Publicados
  { key: 'crm_contracts', label: 'Contratos', icon: 'FileSignature' },
  // v1.9.0 - Permissão granular do módulo de Propostas Comerciais (aba do negócio)
  { key: 'crm_proposals', label: 'Propostas Comerciais', icon: 'FileBadge' },
  // v2.2.0 - Permissão dedicada (fallback global) para cancelar uma proposta JÁ ACEITA.
  // Usada quando o workspace NÃO está com permissões por fase habilitadas.
  // A ação verificada é `can_delete` (ação destrutiva/exceção).
  { key: 'crm_proposals_cancel_accepted', label: 'Cancelar Proposta Aceita', icon: 'FileX' },
  // v2.5.0 - Telas dedicadas do submenu "Propostas" (Kanban + Listagem)
  { key: 'crm_proposals_kanban', label: 'Kanban de Propostas', icon: 'Kanban' },
  { key: 'crm_proposals_list', label: 'Listagem de Propostas', icon: 'List' },


  // v1.6.0 - Permissão granular para Tipos de Documento
  { key: 'crm_document_types', label: 'Tipos de Documento', icon: 'FileCheck' },
  // Menus do módulo Financeiro
  { key: 'financial_dashboard', label: 'Dashboard Financeiro', icon: 'LayoutDashboard' },
  { key: 'financial_charges', label: 'Cobranças', icon: 'Receipt' },
  { key: 'financial_accounts', label: 'Contas Correntes', icon: 'Landmark' },
  { key: 'financial_payment_methods', label: 'Meios de Pagamento', icon: 'Wallet' },
  // v1.7.0 - Permissão granular para Régua de Cobrança
  { key: 'financial_billing_reminders', label: 'Régua de Cobrança', icon: 'Bell' },
  // v1.0.0 — Permissão granular para antecipação de ciclo recorrente (Feature 2)
  // Controla o botão "Antecipar" em ChargeDetailsModal e SubscriptionDetailsModal.
  // Toda antecipação debita imediatamente o cartão padrão do cliente — por isso
  // é uma permissão dedicada, separada de financial_charges.can_edit.
  // Admins (root_admin/tenant_admin) recebem por padrão; supervisores precisam
  // ter `can_create` explicitamente liberado no perfil.
  { key: 'financial_subscription_anticipate', label: 'Antecipar Cobrança Recorrente', icon: 'Rocket' },
  // v1.6.0: Permissão granular para exportação de negócios
  { key: 'crm_deals_export', label: 'Exportar Negócios', icon: 'Download' },
  // v1.0.0 - Permissão granular para Tags de Negócio (CRM)
  // Controla criar/editar/excluir tags de deal no modal e diálogo de seleção (Liberado por padrão)
  { key: 'crm_deal_tags', label: 'Tags de Negócio', icon: 'Tags' },
  // v2.4.0 - Parâmetro: permitir alterar data de conclusão (actual_close_date) de negócios ganhos/perdidos
  { key: 'crm_param_edit_close_date', label: 'Permitir alterar data de conclusão', icon: 'CalendarCheck' },
  // v2.5.0 - Parâmetro: gestão de Origens de Lead customizáveis por tenant
  { key: 'crm_param_lead_sources', label: 'Origens de Lead', icon: 'Megaphone' },
  // v2.6.0 - Parâmetro: gestão de Motivos de Perda customizáveis por tenant
  { key: 'crm_param_loss_reasons', label: 'Motivos de Perda', icon: 'XCircle' },
  // v2.7.0 - Parâmetro: gestão de Tipos de Proposta customizáveis por tenant
  { key: 'crm_param_proposal_types', label: 'Tipos de Proposta', icon: 'FileText' },
  // Menus do módulo Tarefas
  { key: 'tasks_dashboard', label: 'Dashboard Tarefas', icon: 'LayoutDashboard' },
  { key: 'tasks_kanban', label: 'Kanban Tarefas', icon: 'Kanban' },
  { key: 'tasks_list', label: 'Listagem Tarefas', icon: 'List' },
  { key: 'tasks_categories', label: 'Categorias Tarefa', icon: 'FolderTree' },
  { key: 'tasks_phases', label: 'Fases Tarefa', icon: 'Columns' },
  { key: 'tasks_classifications', label: 'Classificações Tarefa', icon: 'Tags' },
  { key: 'tasks_checklist_templates', label: 'Templates de Checklist', icon: 'ListChecks' },

  // Menus do submódulo Retenção (base de Tarefas com task_type = retention).
  // Visíveis apenas quando tenants.retention_enabled = true.
  { key: 'retention_dashboard', label: 'Dashboard Retenção', icon: 'LayoutDashboard' },
  { key: 'retention_kanban', label: 'Kanban Retenção', icon: 'Kanban' },
  { key: 'retention_list', label: 'Listagem Retenção', icon: 'List' },
  { key: 'retention_categories', label: 'Categorias Retenção', icon: 'FolderTree' },
  { key: 'retention_phases', label: 'Fases Retenção', icon: 'Columns' },
  { key: 'retention_classifications', label: 'Classificações Retenção', icon: 'Tags' },
  { key: 'retention_completion_reasons', label: 'Motivos de Conclusão Retenção', icon: 'ListChecks' },

  // Menus do submódulo Jurídico (base de Tarefas com task_type = legal).
  // Visíveis apenas quando tenants.legal_enabled = true.
  { key: 'legal_dashboard', label: 'Dashboard Jurídico', icon: 'LayoutDashboard' },
  { key: 'legal_kanban', label: 'Kanban Jurídico', icon: 'Kanban' },
  { key: 'legal_list', label: 'Listagem Jurídico', icon: 'List' },
  { key: 'legal_categories', label: 'Categorias Jurídico', icon: 'FolderTree' },
  { key: 'legal_phases', label: 'Fases Jurídico', icon: 'Columns' },
  { key: 'legal_classifications', label: 'Classificações Jurídico', icon: 'Tags' },
  { key: 'legal_completion_reasons', label: 'Motivos de Conclusão Jurídico', icon: 'ListChecks' },



  // Menus do módulo Veículos (visíveis apenas quando vehicles_enabled = true)
  { key: 'vehicles_list', label: 'Lista de Veículos', icon: 'Car' },
  { key: 'vehicles_categories', label: 'Categorias de Veículo', icon: 'Tags' },
  // Cadastro único e transversal de Pessoas (fornecedor, fabricante, prestador...).
  { key: 'persons', label: 'Pessoas', icon: 'Contact' },
  // Submenus especialistas do menu Pessoas, independentes de módulos específicos.
  { key: 'persons_suppliers', label: 'Fornecedores', icon: 'Store' },
  { key: 'persons_manufacturers', label: 'Fabricantes', icon: 'Factory' },
  { key: 'persons_providers', label: 'Prestadores', icon: 'Wrench' },
  { key: 'persons_carriers', label: 'Transportadoras', icon: 'Truck' },
  // 2026-10-16: Logística — remessas (ações: ver, criar, editar, apagar/cancelar)
  { key: 'log_shipments', label: 'Remessas', icon: 'Truck' },
  // Gestão Rastreador: cadastros visíveis com stock_enabled.
  { key: 'stock_overview', label: 'Visão Geral', icon: 'LayoutDashboard' },
  { key: 'stock_movements', label: 'Movimentações', icon: 'ArrowRightLeft' },
  // Fase 3: itens do estoque
  { key: 'stock_trackers', label: 'Rastreadores', icon: 'Cpu' },
  { key: 'stock_chips', label: 'Chips', icon: 'Layers' },
  // 2026-10-07: Gestão de Chip — planos de dados vinculados aos chips
  { key: 'stock_data_plans', label: 'Planos de dados', icon: 'Layers' },
  // 2026-10-14: Gestão de Chip — cancelamento em lote (BR-EST-050..057)
  { key: 'stock_chip_cancellations', label: 'Cancelamento de chips', icon: 'Ban' },
  { key: 'stock_kits', label: 'Kits', icon: 'Package' },
  { key: 'stock_locations', label: 'Endereços de Estoque', icon: 'MapPin' },
  { key: 'stock_models', label: 'Modelos de Rastreador', icon: 'Cpu' },
  // 2026-10-11: Homologação de rastreadores
  { key: 'stock_homologations', label: 'Homologação', icon: 'BadgeCheck' },
  // 2026-10-11: Plataformas de rastreamento
  { key: 'stock_platforms', label: 'Plataformas', icon: 'Monitor' },
  // 2026-10-08: módulo Ordem de Serviço (visível com service_orders_enabled)
  { key: 'os_kanban', label: 'Kanban de OS', icon: 'Kanban' },
  { key: 'os_list', label: 'Ordens de Serviço', icon: 'List' },
  { key: 'os_categories', label: 'Categorias de OS', icon: 'Tags' },
  { key: 'os_phases', label: 'Fases de OS', icon: 'Layers' },
  { key: 'os_service_types', label: 'Tipos de Serviço', icon: 'Wrench' },
  // BR-OS-007: capacidades binárias (aba Parâmetros)
  { key: 'os_param_start', label: 'Iniciar Ordem de Serviço', icon: 'Play' },
  { key: 'os_param_reopen', label: 'Reabrir Ordens de Serviço', icon: 'RotateCcw' },
  { key: 'os_param_review', label: 'Revisar Ordem de Serviço', icon: 'CheckCircle' },
  // BR-OS-017: aprovar OS de Instalação quando o veículo já tem kit instalado
  { key: 'os_param_approve_install_with_kit', label: 'Aprovar OS de instalação com kit já instalado', icon: 'ShieldCheck' },
  // RELEASE: 2026-09-26 - Central de vistorias (Veículos > Vistoria)
  { key: 'vehicles_inspections', label: 'Vistorias', icon: 'ClipboardCheck' },
  { key: 'vehicles_inspections_kanban', label: 'Kanban Vistoria', icon: 'Kanban' },

  // Módulo Inteligência de Dados (Integration Hub / Data Hub)
  { key: 'data_connections', label: 'Conexões de Integração', icon: 'Plug' },
  { key: 'data_datasets', label: 'Datasets Externos', icon: 'Database' },
  { key: 'data_explorer', label: 'Explorador de Dados', icon: 'Search' },
  { key: 'data_sync_history', label: 'Histórico de Sincronização', icon: 'History' },
  // Fase 5 (Consulta por placa / Multas) — permissões funcionais (sem menu próprio).
  //   vehicles_plate_lookup: habilita o botão "Buscar" por placa no cadastro.
  //   vehicles_fines_view: habilita visualizar/atualizar multas do veículo.
  { key: 'vehicles_plate_lookup', label: 'Consulta por Placa', icon: 'Search' },

  // Módulo Workflow 2.0 (visível apenas quando workflow_enabled = true)
  //   workflow_processes : ver/criar/editar processos no construtor
  //   workflow_publish   : publicar uma nova versão do processo (ação sensível)
  //   workflow_approvals : resolver aprovações e tarefas humanas do processo
  { key: 'workflow_processes', label: 'Processos (Workflow)', icon: 'Workflow' },
  { key: 'workflow_publish', label: 'Publicar Processo', icon: 'Rocket' },
  { key: 'workflow_approvals', label: 'Aprovações do Processo', icon: 'CheckCircle2' },
  // 2026-10-06: permissões por página dos submenus do Workflow
  { key: 'workflow_pending', label: 'Pendências do Processo', icon: 'ClipboardCheck' },
  { key: 'workflow_executions', label: 'Execuções do Processo', icon: 'Activity' },
  { key: 'workflow_errors', label: 'Erros do Processo', icon: 'AlertOctagon' },
  { key: 'vehicles_fines_view', label: 'Multas de Veículos', icon: 'ReceiptText' },

  // Módulo Checklist (visível apenas quando checklist_enabled = true)
  // RELEASE: 2026-09-26 - Chaves conferidas no banco via has_granular_permission.
  //   checklist_templates  : ver/criar/editar/excluir Template de Checklist
  //   checklist_publish    : publicar nova versão do modelo (ação sensível)
  //   checklist_inspections: vistorias (Fase 2)
  //   checklist_review     : aprovar/reprovar itens da vistoria (Fase 2)
  //   checklist_reopen     : reabrir vistoria finalizada (Fase 2)
  //   checklist_schedules  : vistorias periódicas (Fase 2b)
  { key: 'checklist_templates', label: 'Template de Checklist', icon: 'ClipboardList' },
  { key: 'checklist_publish', label: 'Publicar Template de Checklist', icon: 'Rocket' },
  { key: 'checklist_inspections', label: 'Vistorias', icon: 'ClipboardCheck' },
  { key: 'checklist_review', label: 'Revisar Vistorias', icon: 'CheckCircle2' },
  { key: 'checklist_reopen', label: 'Reabrir Vistorias', icon: 'RotateCcw' },
  { key: 'checklist_schedules', label: 'Vistorias Periódicas', icon: 'CalendarClock' },



  // Menu do módulo Agendamento Online (configuração admin)
  // v2.2.0 - Permissão granular para Agendamento Online (não afeta link público /schedule/...)
  { key: 'scheduling', label: 'Agendamento Online', icon: 'CalendarClock' },
  // Central Unificada de Lembretes — controla apenas a entrada do menu/página.
  // A visibilidade interna das abas continua governada pelas chaves dos módulos
  // donos (ex: `scheduling`, `financial_billing_reminders`).
  { key: 'reminders_center', label: 'Central de Lembretes', icon: 'Bell' },
  // Mensagens Agendadas (chat) — agente vê os próprios; admin vê todos do tenant.
  // Granularidade view/create/edit/delete usada na UI do composer e na lista global.
  { key: 'scheduled_messages', label: 'Mensagens Agendadas', icon: 'Clock' },
  // Menus do módulo Indicação (Fase 5 - Bloco 5G)
  // 8 chaves granulares para permitir atribuição via Perfis de Permissão a supervisores.
  // Admins (root_admin/tenant_admin) têm acesso total automaticamente.
  { key: 'referral_dashboard', label: 'Dashboard Indicação', icon: 'LayoutDashboard' },
  { key: 'referral_programs', label: 'Programas de Indicação', icon: 'Megaphone' },
  { key: 'referral_campaigns', label: 'Campanhas de Indicação', icon: 'Megaphone' },
  { key: 'referral_affiliates', label: 'Afiliados', icon: 'Users' },
  // v2.3.0 - Permissão granular para o menu Indicações (ReferralSidebar /tenant/indicacao/indicacoes)
  { key: 'referral_referrals', label: 'Indicações', icon: 'Inbox' },
  { key: 'referral_materials', label: 'Materiais de Indicação', icon: 'Package' },
  { key: 'referral_commissions', label: 'Comissões de Indicação', icon: 'Receipt' },
  { key: 'referral_payouts', label: 'Pagamentos de Indicação', icon: 'Wallet' },
  // Removido `referral_settings` (v1.1.0): a página dedicada foi descontinuada.
  // Branding/Termos/Defaults passaram a ser editados no modal de Programa,
  // controlado pela permissão `referral_programs` (criar/editar).
  // A entrada "Configurações" da sidebar aponta para /tenant/settings.
  // ============================================================================
  // Permissões granulares da Central de Importação de Listas (uma por aba).
  // Cada aba é mapeada para o módulo correspondente no modal de Perfis de Permissão
  // (lists_contacts -> Conversas | lists_deals/lists_customers -> CRM | lists_affiliates -> Indicação).
  // ============================================================================
  { key: 'lists_contacts', label: 'Importação Lista - Contatos', icon: 'Upload' },
  { key: 'lists_deals', label: 'Importação Lista - Negócios', icon: 'Upload' },
  { key: 'lists_customers', label: 'Importação Lista - Clientes', icon: 'Upload' },
  { key: 'lists_affiliates', label: 'Importação Lista - Afiliados', icon: 'Upload' },
  // Central Unificada de Permissões (v1.0.0) — registro padronizado da key
  // usada pelo PermissionGuard da rota /tenant/permissions. O gate efetivo
  // continua sendo `requireAdmin`; este registro existe apenas para alinhar
  // com a convenção do projeto (toda menuKey passada ao guard deve estar em
  // AVAILABLE_MENUS) e habilitar auditoria/listagens futuras.
  { key: 'permissions', label: 'Central de Permissões', icon: 'Shield' },
  // Base de Conhecimento (Wiki) — F1
  // 8 permissões granulares para o módulo /tenant/kb.
  //   kb_view: ver o módulo; kb_create: criar página/space; kb_edit: editar rascunho;
  //   kb_review: aprovar/recusar em revisão; kb_publish: publicar aprovada;
  //   kb_admin: administrador do módulo; kb_manage_classification: alterar
  //   classificação de dados; kb_manage_taxonomies: gerenciar taxonomias.
  { key: 'kb_view', label: 'Base de Conhecimento - Ver', icon: 'BookOpen' },
  { key: 'kb_create', label: 'Base de Conhecimento - Criar', icon: 'FilePlus' },
  { key: 'kb_edit', label: 'Base de Conhecimento - Editar', icon: 'Edit' },
  { key: 'kb_review', label: 'Base de Conhecimento - Revisar', icon: 'CheckCircle' },
  { key: 'kb_publish', label: 'Base de Conhecimento - Publicar', icon: 'Rocket' },
  { key: 'kb_admin', label: 'Base de Conhecimento - Admin', icon: 'Shield' },
  { key: 'kb_manage_classification', label: 'Base de Conhecimento - Classificação', icon: 'Lock' },
  { key: 'kb_manage_taxonomies', label: 'Base de Conhecimento - Taxonomias', icon: 'Tags' },
  // Extranet KB (portal do cliente/parceiro) — Fase E
  //   kb_extranet: gerenciar usuários externos, acessos e auditoria da extranet.
  { key: 'kb_extranet', label: 'Base de Conhecimento - Extranet', icon: 'Users' },
] as const;

// Menus bloqueados para supervisores (hardcoded)
// "permissions" também é bloqueado para supervisores: a rota é requireAdmin,
// e este bloqueio garante que nem o canView() devolva true mesmo se alguém
// adicionar a permissão indevidamente em um perfil.
export const BLOCKED_MENUS_FOR_SUPERVISOR = ['company', 'settings', 'permissions'];
